<template>
  <loading-content :loading="loading">
    <slot name="top" />
    <card class="mb-5" v-if="detailSearchVisible">
      <h3>Detailne otsing</h3>
      <v-row>
        <v-col>
          <v-select
            v-model="search.status"
            :items="calendarStatusOptions"
            label="Olek"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
           <v-select
              v-model="search.objectType"
              :items="objectTypeOptions"
              label="Objekti tüüp*"
            />
        </v-col>
      </v-row>
    </card>
    <!-- The filters a card sent us here with, none of which has a control on
         this page. -->
    <list-filter-chips :search="search" :labels="filterLabels" @clear="clearFilter" />
    <v-data-table-server
      :headers="plate ? plateColumns : headers"
      :items="events"
      :loading="loading"
      :items-length="count"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :items-per-page-options="itemsPerPageOptions"
    >
      <template v-slot:[`item.startDate`]="{ item }">
        <!-- New look: the date first, then the time, with a small status dot. -->
        <div v-if="plate" class="d-flex align-center ga-2">
          <span class="obj-dot" :class="'bg-' + eventColor(item)" />
          <div>
            <a href="#" class="obj-when" @click.prevent="viewEvent(item)"><b>{{ plateDay(item.startDate) }}</b> {{ plateTime(item) }}</a>
            <div class="text-caption" v-if="item.status === 'deleted'">{{calendarDeleteReasonById(item.deleteReason).text}}</div>
          </div>
        </div>
        <div v-else class="d-flex align-center">
          <v-chip class="mr-1" :color="eventColor(item)" size="small" variant="flat" />
          <div>
            <a href="#" @click="viewEvent(item)">{{eventPeriod(item)}}</a>
            <div class="text-caption" v-if="item.status === 'deleted'">{{calendarDeleteReasonById(item.deleteReason).text}}</div>
          </div>
        </div>
      </template>
      <template v-slot:[`item.client`]="{ item }">
        <client-link :client="item.client" />
      </template>
      <template v-slot:[`item.object`]="{ item }">
        <object-link :object="item.object" :class="{ 'obj-addr-link': plate }" />
        <!-- The equipment at that address, under the address it belongs to. -->
        <div v-if="deviceSummary(item.object.devices)" class="text-caption text-grey">
          {{deviceSummary(item.object.devices)}}
        </div>
      </template>
      <!-- The object page's new look: who in their colour, what was asked
           and how it was paid, in place of the client and the address, which
           are the page's own. -->
      <template v-slot:[`item.user.fullName`]="{ item }">
        <span class="d-inline-flex align-center ga-2">
          <span v-if="plate && item.user" class="obj-ini" :style="initialsStyle(item.user.color)">{{ initialsOf(item.user.fullName) }}</span>
          {{ item.user && item.user.fullName }}<template v-if="item.helper && item.helper.fullName">, {{ item.helper.fullName }}</template>
        </span>
      </template>
      <template v-slot:[`item.comments`]="{ item }">
        <span class="text-medium-emphasis">{{ item.comments || '—' }}</span>
      </template>
      <template v-slot:[`item.payment.type`]="{ item }">
        <!-- A töökäsk still ahead owes nothing yet: no Maksmata until it is done. -->
        <span v-if="item.status === 'future' && item.payment && item.payment.type === 0" class="text-medium-emphasis">—</span>
        <span v-else :class="{ 'obj-unpaid': item.payment && item.payment.type === 0 }">{{ paymentLabel(item.payment && item.payment.type) }}</span>
      </template>
      <template v-slot:[`item.category`]="{ item }">
        {{calendarCategoryById(item.category).text}}
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <button v-if="plate" type="button" class="obj-link" @click="addNote(item)">Lisa märkus</button>
        <v-btn v-else @click="addNote(item)" color="primary" size="small">Lisa märkus</v-btn>
      </template>
    </v-data-table-server>
    <note-form
      :object="note.object"
      :objectId="note.objectId"
      :event="note.event"
      v-model:visible="note.showForm"
    />
    <v-dialog v-model="selectedEvent" max-width="700px">
      <event mode="view" :eventId="selectedEventId" @close="selectedEvent = false" />
    </v-dialog>
  </loading-content>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'
import { pickBy } from 'lodash'
import dayjs from 'dayjs'
import helpers from '@/plugins/helpersMixin'
import listMixin from '@/plugins/listMixin'
import { deviceSummary } from '@/plugins/devices'
import { initials } from '@/components/frame/frameLinks'

const PAYMENT_TYPES = ['Maksmata', 'Arvega', 'Sularaha', 'Tasuta']

export default {
  mixins: [ helpers, listMixin ],
  props: {
    objectId: {
      required: false
    },
    clientId: {
      required: false
    },
    detailSearch: {
      required: false,
      default: false
    },
    // The object page's new look (Seaded -> Objekt uues kujunduses).
    plate: {
      type: Boolean,
      default: false
    }
  },
  data () {
    // The same component is the /calendar/events page and the Kalender tab on
    // an object and on a client. Only the page may take its filters from the
    // address bar: a tab's URL belongs to the record it is showing, and reading
    // it would let an object's page query leak into the list.
    const fromQuery = this.objectId || this.clientId ? () => '' : field => this.$route.query[field]
    return {
      events: [],
      selectedEvent: false,
      selectedEventId: null,
      search: {
        startDate: fromQuery('startDate') || '',
        endDate: '',
        paymentType: fromQuery('paymentType') || '',
        status: '',
        objectType: '',
        // The Töölaud's "Maksmata tööd" card opens this page on its own filter.
        // None of these four has a control here — they are shown, and cleared,
        // as chips above the table.
        daysSince: parseInt(fromQuery('daysSince')) || '',
        technicId: parseInt(fromQuery('technicId')) || '',
        // An array either way: one category arrives from the query as a bare
        // string, and qs would then send it as a scalar the server drops.
        category: [].concat(fromQuery('category') || []),
        // Deleted entries have always been part of this list, and stay part of
        // it. The card counts only work somebody still owes, so the link it
        // builds is the one caller that turns them off.
        deleted: fromQuery('deleted') === 'false' ? false : true,
        // Oldest debt first, as the card ranked it. Sorted by the server:
        // sorting here would only reorder the page in front of us.
        order: fromQuery('order') || ''
      },
      // `startDate` and `deleted` get no chip: the card sends them only to
      // reach back over the whole history and to leave out entries somebody
      // deleted, which is what the reader expects of a debt list anyway.
      filterLabels: {
        daysSince: days => 'Maksmata üle ' + days + ' päeva',
        paymentType: 'Maksmata',
        technicId: 'Ainult minu tööd'
      },
      headers: [{
        title: 'Kuupäev',
        align: 'start',
        value: 'startDate',
        sortable: false
      }, {
        title: 'Tüüp',
        align: 'start',
        value: 'category',
        sortable: false
      }, {
        title: 'Klient',
        align: 'start',
        value: 'client',
        sortable: false
      }, {
        title: 'Address',
        align: 'start',
        value: 'object',
        sortable: false
      }, {
        title: 'Tehnik',
        align: 'start',
        value: 'user.fullName',
        sortable: false
      }, {
        align: 'end',
        title: '',
        value: 'actions',
        sortable: false
      }],
      plateHeaders: [{
        title: 'Aeg',
        align: 'start',
        value: 'startDate',
        sortable: false
      }, {
        title: 'Tüüp',
        align: 'start',
        value: 'category',
        sortable: false
      }, {
        title: 'Tehnik',
        align: 'start',
        value: 'user.fullName',
        sortable: false
      }, {
        title: 'Töökäsk',
        align: 'start',
        value: 'comments',
        sortable: false
      }, {
        title: 'Makse',
        align: 'start',
        value: 'payment.type',
        sortable: false
      }, {
        align: 'end',
        title: '',
        value: 'actions',
        sortable: false
      }],
      note: {
        object: null,
        objectId: null,
        showForm: false
      }
    }
  },
  watch: {
    params: {
      handler() {
        this.refresh()
      },
      deep: true
    }
  },
  computed: {
    // On a client's page the address is no longer the page's own: a client
    // can have several, so it comes back after the type.
    plateColumns () {
      if (!this.clientId) return this.plateHeaders
      const columns = this.plateHeaders.slice()
      columns.splice(2, 0, { title: 'Objekt', align: 'start', value: 'object', sortable: false })
      return columns
    },
    params () {
      return pickBy({
        type: 2,
        // Asking for the deleted ones has to lift the veil that hides them, or
        // the answer is always empty: the "Kustutatud" olek looks for a
        // deletedDate, which paranoid mode has already filtered away.
        ...(this.search.status === 'deleted' ? { deleted: true } : {}),
        objectId: this.objectId,
        clientId: this.clientId,
        limit: this.itemsPerPage,
        offset: (this.page - 1) * this.itemsPerPage,
        ...this.search
      }, v => v !== undefined)
    },
    detailSearchVisible () {
      return this.detailSearch
    }
  },
  methods: {
    initialsOf: initials,
    // A technician's colour behind the initials; a pale one gets dark ink.
    initialsStyle (color) {
      if (!color) return null
      const hex = color.replace('#', '')
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
      const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
      return { background: color, color: (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#16322A' : '#fff' }
    },
    plateDay (date) {
      return dayjs(date).format('DD.MM.YYYY')
    },
    plateTime (event) {
      return dayjs(event.startDate).format('HH:mm') + '–' + dayjs(event.endDate).format('HH:mm')
    },
    paymentLabel (type) {
      return PAYMENT_TYPES[type] || '—'
    },
    // '' is what listMixin reads as "not filtering by this". `category` is a
    // list, so it empties to one.
    clearFilter (field) {
      this.search[field] = field === 'category' ? [] : ''
    },
    deviceSummary,
    ...mapActions(['showNotification']),
    async refresh () {
      this.loading = true
      try {
        const { events, count } = await api.getEvents(this.params)
        this.events = events
        this.count = count
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.loading = false
    },
    addNote (event) {
      this.note.object = event.object
      this.note.objectId = event.object.id
      this.note.event = event
      this.note.showForm = true
    },
    viewEvent (event) {
      this.selectedEventId = event.id
      this.selectedEvent = true
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>